/* global axios */
import ApiClient from './ApiClient';

const reportParams = params => ({
  ...params,
  // Date pickers send the last whole second; APIs use a half-open interval.
  ...(params.until ? { until: Number(params.until) + 1 } : {}),
  timezone: params.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const getTimeOffset = () => -new Date().getTimezoneOffset() / 60;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  createExport(reportType, filters) {
    return axios.post(`${this.url.replace(/\/reports$/, '/report_exports')}`, {
      report_type: reportType,
      filters: reportParams(filters),
    });
  }

  getExport(id) {
    return axios.get(
      `${this.url.replace(/\/reports$/, '/report_exports')}/${id}`
    );
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
  }) {
    return axios.get(`${this.url}`, {
      params: reportParams({
        metric,
        since: from,
        until: to,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      }),
    });
  }

  // eslint-disable-next-line default-param-last
  getSummary(since, until, type = 'account', id, groupBy, businessHours) {
    return axios.get(`${this.url}/summary`, {
      params: reportParams({
        since,
        until,
        type,
        id,
        group_by: groupBy,
        business_hours: businessHours,
        timezone_offset: getTimeOffset(),
      }),
    });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: reportParams({
        type,
        page,
      }),
    });
  }

  getConversationsDashboard(params) {
    return axios.get(`${this.url}/conversations_dashboard`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getConversationsDashboardExport(params) {
    return axios.get(`${this.url}/conversations_dashboard_export`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getLifecycleReport(params) {
    return axios.get(`${this.url}/lifecycle`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getLifecycleReportExport(params) {
    return axios.get(`${this.url}/lifecycle_export`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getAdsReport(params) {
    return axios.get(`${this.url}/ads`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getAdsReportExport(params) {
    return axios.get(`${this.url}/ads_export`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getResponsesReport(params) {
    return axios.get(`${this.url}/responses`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getResponsesReportExport(params) {
    return axios.get(`${this.url}/responses_export`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getResolutionsReport(params) {
    return axios.get(`${this.url}/resolutions`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getResolutionsReportExport(params) {
    return axios.get(`${this.url}/resolutions_export`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getActivityReport(type, params) {
    return axios.get(`${this.url}/${type}`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getActivityReportExport(type, params) {
    return axios.get(`${this.url}/${type}_export`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getPeopleReport(type, params) {
    return axios.get(`${this.url}/${type}`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getPeopleReportExport(type, params) {
    return axios.get(`${this.url}/${type}_export`, {
      params: reportParams({ ...params, timezone_offset: getTimeOffset() }),
    });
  }

  getAgentReports({ from: since, to: until, businessHours, agentId }) {
    const params = { since, until, business_hours: businessHours };
    if (agentId) {
      params.agent_id = agentId;
      params.timezone_offset = getTimeOffset();
    }

    return axios.get(`${this.url}/agents`, {
      params: reportParams(params),
    });
  }

  getAgentAvailability(agentId, { from, to }) {
    return axios.get(`${this.url}/agent_availability`, {
      params: reportParams({
        agent_id: agentId,
        from,
        to: to == null ? to : Number(to) + 1,
        timezone_offset: getTimeOffset(),
      }),
    });
  }

  getConversationsSummaryReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: reportParams({ since, until, business_hours: businessHours }),
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: reportParams({
        timezone_offset: getTimeOffset(),
        days_before: daysBefore,
      }),
    });
  }

  getLabelReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/labels`, {
      params: reportParams({ since, until, business_hours: businessHours }),
    });
  }

  getInboxReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/inboxes`, {
      params: reportParams({ since, until, business_hours: businessHours }),
    });
  }

  getTeamReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/teams`, {
      params: reportParams({ since, until, business_hours: businessHours }),
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: reportParams({ since: from, until: to }),
    });
  }

  getBotSummary({ from, to, groupBy, businessHours } = {}) {
    return axios.get(`${this.url}/bot_summary`, {
      params: reportParams({
        since: from,
        until: to,
        type: 'account',
        group_by: groupBy,
        business_hours: businessHours,
      }),
    });
  }
}

export default new ReportsAPI();
